import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Select } from '@/components/atoms/Select';
import { Badge } from '@/components/atoms/Badge';
import { Card } from '@/components/molecules/Card';
import { DataTable } from '@/components/organisms/DataTable';
import type { DataTableColumn } from '@/components/organisms/DataTable';
import { useBackofficeConversationPractices } from '@/hooks/useConversationData';
import type { ConversationPracticeListItem } from '@/types/conversation.types';
import { ROUTES, buildRoute } from '@/constants/routes';
import styles from './ConversationListPage.module.scss';

const STATUS_LABELS: Record<string, string> = {
  published: 'Publicado',
  draft: 'Borrador',
  archived: 'Archivado',
};

/**
 * Lista y acciones solo para prácticas de conversación (rutas `/backoffice/conversations/*`).
 * Lista y acciones para prácticas de conversación (`/backoffice/conversations/*`). API dedicada.
 */
export function ConversationListPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const { resources, isLoading } = useBackofficeConversationPractices();

  const filteredResources = useMemo(
    () =>
      resources.filter((resource) => {
        const matchesSearch =
          resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          resource.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesLevel = levelFilter === 'all' || resource.level === levelFilter;
        const matchesStatus = statusFilter === 'all' || resource.status === statusFilter;
        return matchesSearch && matchesLevel && matchesStatus;
      }),
    [resources, searchQuery, levelFilter, statusFilter]
  );

  const columns: DataTableColumn<ConversationPracticeListItem>[] = [
    {
      key: 'title',
      header: 'Título',
      accessor: (row) => row.title,
      sortable: true,
      width: '32%',
    },
    {
      key: 'level',
      header: 'Nivel',
      accessor: (row) => <Badge>{row.level}</Badge>,
      sortable: true,
    },
    {
      key: 'status',
      header: 'Estado',
      accessor: (row) => {
        const variant =
          row.status === 'published'
            ? 'success'
            : row.status === 'archived'
              ? 'default'
              : 'default';
        return <Badge variant={variant}>{STATUS_LABELS[row.status] ?? row.status}</Badge>;
      },
      sortable: true,
    },
    {
      key: 'activitiesCount',
      header: 'Actividades',
      accessor: (row) => row.activitiesCount.toString(),
      sortable: true,
      align: 'right',
    },
    {
      key: 'contentCount',
      header: 'Contenidos',
      accessor: (row) => row.contentCount.toString(),
      sortable: true,
      align: 'right',
    },
    {
      key: 'updatedAt',
      header: 'Actualizado',
      accessor: (row) => new Date(row.updatedAt).toLocaleDateString('es-ES'),
      sortable: true,
    },
  ];

  return (
    <div className={styles.resourceList}>
      <div className={styles.resourceList__header}>
        <div>
          <h1>Conversaciones</h1>
          <p className={styles.resourceList__subtitle}>
            Prácticas guiadas (diálogo interactivo). Flujo independiente de Recursos.
          </p>
        </div>
        <div className={styles.resourceList__actions}>
          <Button
            variant="ghost"
            leftIcon={<Sparkles size={16} />}
            onClick={() => navigate(ROUTES.BACKOFFICE.CONVERSATIONS_GENERATE)}
          >
            Generar con IA
          </Button>
          <Button leftIcon={<Plus />} onClick={() => navigate(ROUTES.BACKOFFICE.CONVERSATION_CREATE)}>
            Crear manual
          </Button>
        </div>
      </div>

      <Card className={styles.resourceList__filters}>
        <div className={styles.resourceList__filtersGrid}>
          <Input
            type="search"
            placeholder="Buscar conversaciones..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Select
            placeholder="Todos los niveles"
            value={levelFilter}
            onValueChange={setLevelFilter}
            options={[
              { label: 'Todos los niveles', value: 'all' },
              { label: 'A1', value: 'A1' },
              { label: 'A2', value: 'A2' },
              { label: 'B1', value: 'B1' },
              { label: 'B2', value: 'B2' },
              { label: 'C1', value: 'C1' },
              { label: 'C2', value: 'C2' },
            ]}
          />
          <Select
            placeholder="Todos los estados"
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={[
              { label: 'Todos los estados', value: 'all' },
              { label: 'Publicado', value: 'published' },
              { label: 'Borrador', value: 'draft' },
              { label: 'Archivado', value: 'archived' },
            ]}
          />
        </div>
      </Card>

      <DataTable
        columns={columns}
        data={filteredResources}
        onRowClick={(row) => navigate(buildRoute(ROUTES.BACKOFFICE.CONVERSATION_EDIT, { id: row.id }))}
        isLoading={isLoading}
        emptyMessage="No hay conversaciones publicadas o en borrador"
      />
    </div>
  );
}

export default ConversationListPage;
